import React, { useState, useEffect, useRef } from 'react';
import { getSignedUrlFromPath, refreshSignedUrl, BUCKET_NAMES } from '@/lib/supabase-s3-storage';

type BucketType = keyof typeof BUCKET_NAMES;

interface SecureImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  bucket?: BucketType;
  fallbackSrc?: string;
  onSignedUrlGenerated?: (signedUrl: string) => void;
}

/**
 * SecureImage component that handles private Supabase storage images
 * Automatically generates signed URLs for file references and refreshes expired URLs
 */
export const SecureImage: React.FC<SecureImageProps> = ({
  src,
  bucket,
  fallbackSrc,
  onSignedUrlGenerated,
  onError,
  ...imgProps
}) => {
  // Initialize displaySrc properly - don't use original src for storage URLs
  const [displaySrc, setDisplaySrc] = useState<string>(() => {
    // If it's a storage URL, start with empty until signed URL is generated
    if (src?.startsWith('supabase-storage://')) {
      return '';
    }
    return src || '';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isMountedRef = useRef(true);
  const currentSrcRef = useRef(src);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Update the ref when src changes
  useEffect(() => {
    currentSrcRef.current = src;
    isMountedRef.current = true;
    
    // Abort any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Reset state when src changes
    setHasError(false);
    
    // Reset displaySrc for new storage URLs
    if (src?.startsWith('supabase-storage://')) {
      setDisplaySrc('');
    } else {
      setDisplaySrc(src || '');
    }
  }, [src]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const generateSignedUrl = async () => {
      if (!src) {
        setDisplaySrc('');
        return;
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        // Check if this is a file reference from our storage
        if (src.startsWith('supabase-storage://')) {
          setIsLoading(true);
          
          
          
          // Extract bucket and path from the reference
          const referenceMatch = src.match(/^supabase-storage:\/\/([^/]+)\/(.+)$/);
          if (!referenceMatch) {
            throw new Error('Invalid storage reference format');
          }
          
          const [, bucketName, filePath] = referenceMatch;
          
          // Determine bucket type from bucket name (auto-detect from storage reference)
          let bucketType: BucketType;
          if (bucketName === 'private-project-images') bucketType = 'project-images';
          else if (bucketName === 'private-profile-avatars') bucketType = 'profile-avatars';
          else if (bucketName === 'private-site-photos') bucketType = 'site-photos';
          else if (bucketName === 'private-documents') bucketType = 'documents';
          else throw new Error(`Unknown bucket: ${bucketName}`);
          
          
          
          // Check if request was aborted
          if (signal.aborted) {
            
            return;
          }
          
          const signedUrl = await getSignedUrlFromPath(filePath, bucketType);
          
          
          
          // Check again if request was aborted or component unmounted
          if (signal.aborted || !isMountedRef.current || currentSrcRef.current !== src) {
            
            return;
          }
          
          
          
          
          setDisplaySrc(signedUrl);
          onSignedUrlGenerated?.(signedUrl);
          
          
        } else if (src.includes('/storage/v1/object/') && bucket) {
          // This is an existing Supabase storage URL, refresh it
          setIsLoading(true);
          
          
          // Check if request was aborted
          if (signal.aborted) {
            
            return;
          }
          
          const refreshedUrl = await refreshSignedUrl(src, bucket);
          
          // Check again if request was aborted or component unmounted
          if (signal.aborted || !isMountedRef.current || currentSrcRef.current !== src) {
            
            return;
          }
          
          setDisplaySrc(refreshedUrl);
          onSignedUrlGenerated?.(refreshedUrl);
          
        } else {
          // Regular URL, use as-is
          
          if (!signal.aborted && isMountedRef.current && currentSrcRef.current === src) {
            setDisplaySrc(src);
          }
        }
      } catch (error) {
        // Don't process errors for aborted requests
        if (signal.aborted) {
          
          return;
        }
        
        
        if (isMountedRef.current && currentSrcRef.current === src) {
          setHasError(true);
          
          // Use fallback or show error state
          if (fallbackSrc) {
            setDisplaySrc(fallbackSrc);
          } else {
            setDisplaySrc(''); // Don't use the invalid src
          }
        }
      } finally {
        if (!signal.aborted && isMountedRef.current && currentSrcRef.current === src) {
          setIsLoading(false);
        }
      }
    };

    generateSignedUrl();
  }, [src, bucket, fallbackSrc, onSignedUrlGenerated]);

  const handleError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    
    
    
    
    setHasError(true);
    
    // If we have a fallback and haven't tried it yet
    if (fallbackSrc && displaySrc !== fallbackSrc) {
      
      setDisplaySrc(fallbackSrc);
      return;
    }
    
    // Call the original onError if provided
    onError?.(event);
  };

  const handleLoad = () => {
    
    
    setHasError(false);
  };

  // Additional debugging
  

  // Don't render img tag if we don't have a valid displaySrc yet for storage URLs
  if (src?.startsWith('supabase-storage://') && !displaySrc && !hasError) {
    return (
      <div 
        style={{
          ...imgProps.style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
          border: '1px solid #e0e0e0',
          minHeight: '100px',
          color: '#666',
          fontSize: '14px',
        }}
        className={imgProps.className}
      >
        {isLoading ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '8px' }}>⏳</div>
            <div>Loading image...</div>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '8px' }}>📷</div>
            <div>Image not available</div>
          </div>
        )}
      </div>
    );
  }

  // Don't render anything if we don't have any src to show
  if (!displaySrc) {
    return null;
  }

  // Ensure we don't pass an empty string as src to prevent capacitor:// fallback
  const safeSrc = displaySrc.trim() || undefined;
  
  if (!safeSrc) {
    return null;
  }

  return (
    <img
      {...imgProps}
      src={safeSrc}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
};

export default SecureImage; 